import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { isNavigationFailure, NavigationFailureType } from 'vue-router';
import { getEcho } from '@/plugins/echo';
import { apiRequest } from '@/services/apiClient';
import router from '@/router';
import { normalizeNotification } from '@/utils/notificationUtils';
import type { NotificationItem, RawNotification } from '@/types/notifications';

type NotificationsResponse = {
    data: RawNotification[];
    unread_count?: number;
};

type UnreadCountResponse = {
    unread_count: number;
};

export const useNotificationsStore = defineStore('notifications', () => {
    const notifications = ref<NotificationItem[]>([]);
    const unreadCount = ref(0);
    const isLoading = ref(false);
    const isLoadingMore = ref(false);
    const hasMore = ref(false);
    const isDrawerOpen = ref(false);

    const PAGE_SIZE = 15;
    const restCount = ref(0);

    const hasUnread = computed(() => unreadCount.value > 0);

    async function fetchNotifications(limit = PAGE_SIZE, offset = 0) {
        isLoading.value = true;
        try {
            const response = await apiRequest<NotificationsResponse>('/api/notifications', 'GET', {
                params: { limit, offset },
            });
            notifications.value = response.data.map(normalizeNotification);
            restCount.value = response.data.length;
            hasMore.value = response.data.length >= limit;
            if (typeof response.unread_count === 'number') {
                unreadCount.value = response.unread_count;
            }
        } finally {
            isLoading.value = false;
        }
    }

    async function loadMore() {
        if (!hasMore.value || isLoadingMore.value || isLoading.value) return;
        isLoadingMore.value = true;
        try {
            const response = await apiRequest<NotificationsResponse>('/api/notifications', 'GET', {
                params: { limit: PAGE_SIZE, offset: restCount.value },
            });
            notifications.value.push(...response.data.map(normalizeNotification));
            restCount.value += response.data.length;
            hasMore.value = response.data.length >= PAGE_SIZE;
        } finally {
            isLoadingMore.value = false;
        }
    }

    async function fetchUnreadCount() {
        const response = await apiRequest<UnreadCountResponse>('/api/notifications/unread-count', 'GET');
        unreadCount.value = response.unread_count;
    }

    async function markAsRead(id: string) {
        await apiRequest<void>(`/api/notifications/${id}/read`, 'POST');
        const item = notifications.value.find((n) => n.id === id);
        if (item && !item.isRead) {
            item.isRead = true;
            unreadCount.value = Math.max(0, unreadCount.value - 1);
        }
    }

    async function markAllAsRead() {
        await apiRequest<void>('/api/notifications/read-all', 'POST');
        notifications.value.forEach((n) => {
            n.isRead = true;
        });
        unreadCount.value = 0;
    }

    function listenForNewNotifications(userId: number) {
        const channel = getEcho().private(`App.Models.User.${userId}`);

        channel.error((error: unknown) => {
            console.error('[Notifications] Channel subscription error:', error);
        });

        channel.listen('.NotificationCreated', (notification: RawNotification) => {
            notifications.value.unshift(normalizeNotification({ ...notification, read_at: null }));
            unreadCount.value++;
        });
    }

    function stopListening(userId: number) {
        getEcho().leave(`App.Models.User.${userId}`);
    }

    function openDrawer() {
        isDrawerOpen.value = true;
        void fetchNotifications();
    }

    const closeDrawer = () => { isDrawerOpen.value = false; };

    async function handleNotificationClick(id: string) {
        const item = notifications.value.find((n) => n.id === id);
        if (!item) return;

        if (!item.isRead) {
            await markAsRead(id);
        }

        if (item.href) {
            closeDrawer();
            const failure = await router.push(item.href);
            if (isNavigationFailure(failure, NavigationFailureType.duplicated)) {
                const resolved = router.resolve(item.href);
                await router.replace({
                    path: resolved.path,
                    query: { ...resolved.query, _r: String(Date.now()) },
                });
            }
        }
    }

    return {
        notifications,
        unreadCount,
        isLoading,
        isLoadingMore,
        hasMore,
        isDrawerOpen,
        hasUnread,
        fetchNotifications,
        loadMore,
        fetchUnreadCount,
        markAsRead,
        markAllAsRead,
        handleNotificationClick,
        listenForNewNotifications,
        stopListening,
        openDrawer,
        closeDrawer,
    };
});
